<template>
	<UserPageLayout :loading="loading" :error="error">
		<table class="generic-table">
			<thead>
				<tr>
					<th>Label</th>
					<th>Client secret</th>
					<th>Created</th>
					<th>Actions</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="mtproxy in mtproxies" :key="mtproxy.label">
					<td>{{ mtproxy.label }}</td>
					<td>{{ mtproxy.client_secret }}</td>
					<td>{{ $formatDate(mtproxy.created) }}</td>
					<td>
						<div class="generic-table-actions">
							<button
								@click="deleteMTProxy(mtproxy.label)"
								class="generic-table-action-button"
								:disabled="action_loading"
							>
								Delete
							</button>
						</div>
					</td>
				</tr>
				<tr>
					<td>
						<input
							v-model="newLabel"
							type="text"
							:placeholder="`Label ${labelRegex} (${minLabelLength}-${maxLabelLength})`"
							:minlength="minLabelLength"
							:maxlength="maxLabelLength"
							@input="filterLabelInput"
							@keyup.enter="addMTProxy"
							:disabled="action_loading"
						/>
					</td>
					<td colspan="2"></td>
					<td>
						<button
							@click="addMTProxy"
							class="generic-table-action-button"
							:disabled="action_loading || !labelMeetsLengthRules"
						>
							Add
						</button>
					</td>
				</tr>
			</tbody>
		</table>
	</UserPageLayout>
</template>

<script>
import UserPageLayout from './_PageLayout.vue'

export default {
	name: 'UserMTProxy',
	components: {
		UserPageLayout
	},
	data() {
		return {
			loading: true,
			action_loading: false,
			error: '',
			mtproxies: [],
			newLabel: '',
			labelRegex: '',
			minLabelLength: 1,
			maxLabelLength: 256
		}
	},
	computed: {
		labelMeetsLengthRules() {
			const len = this.newLabel.trim().length
			return len >= this.minLabelLength && len <= this.maxLabelLength
		}
	},
	async mounted() {
		await this.fetchMTProxy()
	},
	methods: {
		async fetchMTProxy() {
			this.loading = true
			this.error = ''

			const authToken = localStorage.getItem('authToken')?.trim()
			if (!authToken || authToken === 'null') {
				this.$router.push({ name: 'Login' })
				return
			}

			try {
				const response = await this.$api.post('/api/user/mtproxy', {
					authToken: authToken
				})

				this.mtproxies = response.data.mtproxies || []
				this.labelRegex = response.data.label_regex
				this.minLabelLength = response.data.min_label_length
				this.maxLabelLength = response.data.max_label_length
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else {
					this.error = 'An error occurred while loading MTProxy entries.'
					console.error('Error:', error)
				}
			} finally {
				this.loading = false
			}
		},
		filterLabelInput(event) {
			const newValue = event.target.value
			try {
				const regex = new RegExp(this.labelRegex, 'g')
				const match = newValue.match(regex)
				const validValue = match ? match.join('') : ''
				this.newLabel = validValue
			} catch (e) {
				// If regex is invalid, don't filter
				this.newLabel = newValue
			}
		},
		async addMTProxy() {
			if (!this.newLabel.trim()) {
				return
			}

			this.action_loading = true
			this.error = ''
			const authToken = localStorage.getItem('authToken')?.trim()

			try {
				await this.$api.post('/api/user/mtproxy/add', {
					authToken: authToken,
					label: this.newLabel.trim()
				})
				this.newLabel = ''
				await this.fetchMTProxy()
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else if (error.response && error.response.status === this.$StatusCodes.BAD_REQUEST) {
					this.error = 'Maximum MTProxy labels reached for your account.'
				} else if (error.response && error.response.status === this.$StatusCodes.CONFLICT) {
					this.error = 'This label is already in use.'
				} else {
					this.error = 'An error occurred while adding MTProxy entry.'
					console.error('Error:', error)
				}
			} finally {
				this.action_loading = false
			}
		},
		async deleteMTProxy(label) {
			this.action_loading = true
			this.error = ''
			const authToken = localStorage.getItem('authToken')?.trim()

			try {
				await this.$api.post('/api/user/mtproxy/delete', {
					authToken: authToken,
					label: label
				})
				await this.fetchMTProxy()
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else if (error.response && error.response.status === this.$StatusCodes.NOT_FOUND) {
					this.error = 'MTProxy entry not found.'
				} else {
					this.error = 'An error occurred while deleting MTProxy entry.'
					console.error('Error:', error)
				}
			} finally {
				this.action_loading = false
			}
		}
	}
}
</script>

<style scoped>
input {
	width: 100%;
	padding: 8px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 14px;
	box-sizing: border-box;
	line-height: 1.5;
	min-width: 200px;
}

input::placeholder {
	font-weight: bold;
	font-size: 12px;
}
</style>
